import React, { useState, useEffect } from 'react';
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import HeaderBar from "./component/HeaderBar";
import CouponTable from "./component/CouponTable";
import CouponController from '../../../controllers/couponController';
import { Switch, Stack } from '@mui/material';
import EditIcon from '../../../assets/images/icons/edit.svg';
import DeleteIcon from '@mui/icons-material/Delete';
import EditCouponModal from './component/EditCouponModal';

interface FormattedCoupon {
    id: string;
    campaignName: string;
    couponCode: string;
    type: string;
    discount: string;
    minimum: string;
    maximum: string;
    category: string;
    validFromDateTime: string;
    validTillDateTime: string;
    published: boolean;
    edit: string;
}

function CouponList() {
    const {typography, palette} = theme;
    const [coupons, setCoupons] = useState<FormattedCoupon[]>([]);
    const [filteredCoupons, setFilteredCoupons] = useState<FormattedCoupon[]>([]);
    const [loading, setLoading] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);
    const [statusUpdating, setStatusUpdating] = useState<{[key: string]: boolean}>({});

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const apiResponse: any = await CouponController.getMyCoupons();
            
            const formattedCoupons: FormattedCoupon[] = apiResponse.data.data.data.map((coupon: any) => ({
                id: coupon.id,
                campaignName: coupon.campaignName,
                couponCode: coupon.couponCode,
                type: coupon.couponType || '-',
                // Flat coupons show ₹, percentage coupons show %.
                discount: (coupon.couponType || '').includes('FLAT')
                    ? `₹${coupon.percentageFlat ?? 0}`
                    : `${coupon.percentageFlat ?? 0}%`,
                minimum: `₹${coupon.minimumPrice ?? 0}`,
                maximum: coupon.maximumPrice != null ? `₹${coupon.maximumPrice}` : '-',
                category: coupon.masterCategories?.[0]?.name || 'N/A',
                validFromDateTime: new Date(coupon.validFromDateTime).toLocaleDateString(),
                validTillDateTime: new Date(coupon.validTillDateTime).toLocaleDateString(),
                published: coupon.status === 'ACTIVE',
                edit: "edit"
            }));
            
            setCoupons(formattedCoupons);
            setFilteredCoupons(formattedCoupons);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, []);

    const handleFilterChange = (filters: { couponCode?: string; campaignName?: string }) => {
        console.log('Received filters:', filters);
        console.log('Current coupons:', coupons);
        
        let filtered = [...coupons];
        
        if (filters.couponCode) {
            const searchTerm = filters.couponCode.toLowerCase();
            filtered = filtered.filter(coupon => 
                (coupon.couponCode ?? '').toLowerCase().includes(searchTerm)
            );
            console.log('After coupon code filter:', filtered);
        }
        
        if (filters.campaignName) {
            const searchTerm = filters.campaignName.toLowerCase();
            filtered = filtered.filter(coupon => 
                (coupon.campaignName ?? '').toLowerCase().includes(searchTerm)
            );
            console.log('After campaign name filter:', filtered);
        }
        
        console.log('Final filtered results:', filtered);
        setFilteredCoupons(filtered);
    };

    const handleStatusChange = async (couponId: string, currentStatus: boolean) => {
        try {
            setStatusUpdating(prev => ({ ...prev, [couponId]: true }));
            
            const newStatus = currentStatus ? 'INACTIVE' : 'ACTIVE';
            await CouponController.updateCouponStatus(couponId, { status: newStatus });
            
            const updatedCoupons = coupons.map(coupon => 
                coupon.id === couponId 
                    ? { ...coupon, published: !currentStatus } 
                    : coupon
            );
            
            setCoupons(updatedCoupons);
            setFilteredCoupons(updatedCoupons);
            
            console.log(`Coupon ${couponId} status updated to ${newStatus}`);
        } catch (error) {
            console.error('Error updating coupon status:', error);
            fetchCoupons();
        } finally {
            setStatusUpdating(prev => ({ ...prev, [couponId]: false }));
        }
    };

    const tableData = {
        columns: [
            {
                Header: "Campaign name",
                accessor: "campaignName",
                width: "30%"
            },
            {Header: "code", accessor: "couponCode"},
            {Header: "Type", accessor: "type"},
            {Header: "Discount", accessor: "discount"},
            {Header: "Min Order", accessor: "minimum"},
            {Header: "Max", accessor: "maximum"},
            {Header: "category", accessor: "category"},
            {Header: "Start Date", accessor: "validFromDateTime"},
            {Header: "End Date", accessor: "validTillDateTime"},
            {
                Header: "published",
                accessor: "published",
                Cell: ({ value, row }: { value: boolean, row: any }) => (
                    <Switch 
                        checked={value} 
                        color="success" 
                        disabled={statusUpdating[row.original.id] || false}
                        onChange={() => handleStatusChange(row.original.id, value)}
                    />
                )
            },
            {
                Header: "Actions",
                accessor: "actions",
                Cell: ({ row }: any) => (
                    <Stack direction="row" spacing={1}>
                        <FaiBox 
                            component={"img"} 
                            src={EditIcon} 
                            height="29px" 
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleEditClick(row.original.id)}
                        />
                        <DeleteIcon 
                            sx={{ 
                                cursor: 'pointer', 
                                color: 'error.main',
                                height: '29px',
                                width: 'auto'
                            }}
                            onClick={() => handleDeleteClick(row.original.id)}
                        />
                    </Stack>
                )
            },
        ],
        rows: filteredCoupons
    };

    const handleEditClick = (couponId: string) => {
        console.log('Edit clicked for coupon ID:', couponId);
        setSelectedCouponId(couponId);
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedCouponId(null);
    };

    const handleCouponUpdated = () => {
        // Refresh the coupon list after update
        fetchCoupons();
    };

    const handleDeleteClick = async (couponId: string) => {
        try {
            // TODO: Add confirmation dialog before deleting
            await CouponController.deleteCoupon(couponId);
            // Refresh the coupon list after deletion
            fetchCoupons();
        } catch (error) {
            console.error('Error deleting coupon:', error);
        }
    };

    return (
        <>
            <FaiBox className={"coupon-list"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                    Coupons
                </FaiTypography>
                <FaiBox className={"coupon-list-content"}>
                    <FaiBox className={"coupon-list-content-header"}>
                        <HeaderBar onFilterChange={handleFilterChange}/>
                    </FaiBox>
                    <FaiBox className={"coupon-list-content-body"} mt={3}>
                        <CouponTable data={tableData}/>
                    </FaiBox>
                </FaiBox>
            </FaiBox>

            <EditCouponModal
                open={editModalOpen}
                onClose={handleCloseEditModal}
                couponId={selectedCouponId}
                onCouponUpdated={handleCouponUpdated}
            />
        </>
    );
}

export default CouponList;
